<template>
	<!-- Carteles arriba a la derecha. Dos clases, con el mismo diseño:
	     · notificaciones de la API (Pusher): clic = abrir; se van a los 8 s;
	     · avisos de la página (avisar() de Utils/notificaciones: "Cambios
	       guardados", "No se pudo guardar"…), con un botón de acción opcional.
	     Con el mouse o el foco encima se quedan (para que dé tiempo a leerlos). -->
	<div class="ntt" aria-live="polite" aria-atomic="false" @mouseenter="pausar" @mouseleave="seguir" @focusin="pausar" @focusout="seguir">
		<transition-group name="ntt" tag="div" class="ntt-stack">
			<div v-for="n in toasts" :key="n.id + ':' + n.cantidad" :class="['ntt-card', `ntt-card--${icono(n)}`]" :role="n.local && n.tipo === 'error' ? 'alert' : 'status'">
				<component :is="n.local ? 'div' : 'button'" :type="n.local ? null : 'button'" :class="['ntt-main', { 'ntt-main--link': !n.local }]" @click="n.local ? null : abrir(n)">
					<span class="ntt-ico" v-html="ICON[icono(n)] || ICON.msg"></span>
					<span class="ntt-txt">
						<span class="ntt-title">{{ texto(n).titulo }}</span>
						<span v-if="texto(n).cuerpo" class="ntt-body">{{ texto(n).cuerpo }}</span>
						<span v-if="!n.local" class="ntt-cta">{{ $t('notificaciones.ver') }}<span aria-hidden="true">→</span></span>
					</span>
				</component>
				<div v-if="n.local && n.accion" class="ntt-acts">
					<button type="button" class="ntt-act" @click="accionar(n)">{{ n.accion.label }}</button>
				</div>
				<button type="button" class="ntt-close" :aria-label="$t('notificaciones.cerrar')" @click="cerrar(n)" v-html="ICON.close"></button>
			</div>
		</transition-group>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import ICON from './iconos';
import { textoNotificacion, familia } from './textos';
import { abrir } from '@/Utils/notificaciones';

const DURACION = 8000;
const ICONO_LOCAL = { ok: 'ok', error: 'x', info: 'info' };

export default {
	name: 'NotificacionesToasts',
	data() {
		return { ICON, pausado: false };
	},
	computed: {
		...mapState('notificaciones', ['toasts'])
	},
	created() {
		this.timers = {};
	},
	watch: {
		toasts(lista) {
			const vivos = new Set(lista.map(t => `${t.id}:${t.cantidad}`));
			Object.keys(this.timers).forEach(k => { if (!vivos.has(k)) { clearTimeout(this.timers[k]); delete this.timers[k]; } });
			if (!this.pausado) lista.forEach(t => this.programar(t));
		}
	},
	beforeDestroy() {
		Object.values(this.timers).forEach(clearTimeout);
	},
	methods: {
		duracion(t) {
			if (t.duracion) return t.duracion;
			if (t.local) return t.accion ? 10000 : (t.tipo === 'error' ? 8000 : 6000);
			return DURACION;
		},
		programar(t) {
			const k = `${t.id}:${t.cantidad}`;
			if (this.timers[k]) return;
			this.timers[k] = setTimeout(() => {
				delete this.timers[k];
				this.$store.commit('notificaciones/quitarToast', t.id);
			}, this.duracion(t));
		},
		pausar() {
			this.pausado = true;
			Object.values(this.timers).forEach(clearTimeout);
			this.timers = {};
		},
		seguir() {
			this.pausado = false;
			this.toasts.forEach(t => this.programar(t));
		},
		texto(n) {
			if (n.local) return { titulo: n.titulo, cuerpo: n.cuerpo };
			// eslint-disable-next-line no-unused-expressions
			this.$i18n.locale;
			return textoNotificacion(n, this.$i18n);
		},
		icono(n) {
			return n.local ? (ICONO_LOCAL[n.tipo] || 'ok') : familia(n);
		},
		abrir(n) {
			abrir(n);
		},
		accionar(n) {
			const fn = n.accion && n.accion.fn;
			this.cerrar(n);
			if (typeof fn === 'function') fn();
		},
		cerrar(n) {
			this.$store.commit('notificaciones/quitarToast', n.id);
		}
	}
};
</script>

<style lang="scss" scoped>
.ntt {
	position: fixed; top: 88px; right: 20px; z-index: 1100;
	width: 380px; max-width: calc(100vw - 24px);
	pointer-events: none;
	font-family: 'DM Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
	-webkit-font-smoothing: antialiased;
}
.ntt-stack { display: flex; flex-direction: column; gap: 12px; }
.ntt button { font: inherit; }
.ntt button:focus { outline: none; }
.ntt button:focus-visible { outline: none; box-shadow: 0 0 0 4px rgba(255, 122, 0, .22); }

/* Tarjeta: blanca, translúcida y con sombra suave (como los formularios) */
.ntt-card {
	--tint: #fff3e8; --ink-ico: #c45a06;
	position: relative; pointer-events: auto;
	border-radius: 20px;
	background: rgba(255, 255, 255, .9);
	-webkit-backdrop-filter: blur(18px) saturate(1.4); backdrop-filter: blur(18px) saturate(1.4);
	border: 1px solid rgba(29, 26, 23, .06);
	box-shadow: 0 1px 2px rgba(29, 26, 23, .04), 0 14px 34px -16px rgba(122, 57, 0, .28), 0 32px 64px -36px rgba(29, 26, 23, .28);
	overflow: hidden;
}
/* Un brillo muy leve del color del aviso, arriba a la izquierda */
.ntt-card::before {
	content: ''; position: absolute; inset: 0; pointer-events: none;
	background: radial-gradient(120% 90% at 0% 0%, var(--tint) 0%, rgba(255, 255, 255, 0) 55%);
	opacity: .75;
}
.ntt-card--msg  { --tint: #e8f0ff; --ink-ico: #2b5fb4; }
.ntt-card--cal  { --tint: #fff1e4; --ink-ico: #c45a06; }
.ntt-card--ok   { --tint: #e6f7ee; --ink-ico: #1c7c45; }
.ntt-card--x    { --tint: #fdebe8; --ink-ico: #c0392b; }
.ntt-card--pay  { --tint: #efeaff; --ink-ico: #6d3ce0; }
.ntt-card--home { --tint: #fff0e3; --ink-ico: #d4610a; }
.ntt-card--info { --tint: #f2eee9; --ink-ico: #5d554e; }

.ntt-main {
	position: relative; display: flex; align-items: flex-start; gap: 14px; width: 100%;
	padding: 16px 46px 16px 16px; margin: 0; border: 0; background: none;
	text-align: left; color: #1d1a17;
}
.ntt-main--link { cursor: pointer; transition: background .2s; }
.ntt-main--link:hover { background: rgba(255, 247, 239, .7); }
.ntt-main--link:hover .ntt-cta span { transform: translateX(3px); }
.ntt-ico {
	display: grid; place-items: center; flex: none; width: 42px; height: 42px; border-radius: 14px;
	background: linear-gradient(145deg, #fff, var(--tint));
	box-shadow: inset 0 0 0 1px rgba(29, 26, 23, .05), 0 6px 14px -8px rgba(29, 26, 23, .25);
	color: var(--ink-ico);
	::v-deep svg { width: 20px; height: 20px; }
}
.ntt-txt { display: flex; flex-direction: column; gap: 3px; min-width: 0; padding-top: 1px; }
.ntt-title { font-size: 14.5px; font-weight: 700; line-height: 1.35; letter-spacing: -.005em; }
.ntt-body {
	font-size: 13.5px; line-height: 1.45; color: #5d554e;
	display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; overflow-wrap: anywhere;
}
.ntt-cta { display: inline-flex; align-items: center; gap: 5px; margin-top: 4px; font-size: 13px; font-weight: 700; color: #e8640a; }
.ntt-cta span { display: inline-block; transition: transform .2s; }

.ntt-acts { position: relative; display: flex; justify-content: flex-end; gap: 8px; padding: 0 16px 14px 72px; margin-top: -4px; }
.ntt-act {
	height: 34px; padding: 0 16px; margin: 0;
	border: 0; border-radius: 999px; background: #fff1e4; color: #c45a06;
	font-size: 13px; font-weight: 700; cursor: pointer;
	transition: background .2s, color .2s, transform .15s;
	&:hover { background: linear-gradient(180deg, #ff8414, #f26b05); color: #fff; }
	&:active { transform: scale(.97); }
}

.ntt-close {
	position: absolute; top: 12px; right: 12px;
	display: grid; place-items: center; width: 30px; height: 30px; padding: 0; margin: 0;
	border: 0; border-radius: 50%; background: transparent; color: #9a918a; cursor: pointer;
	transition: background .2s, color .2s;
	&:hover { background: rgba(29, 26, 23, .06); color: #1d1a17; }
	::v-deep svg { width: 14px; height: 14px; }
}

.ntt-enter-active { transition: transform .45s cubic-bezier(.22, 1.2, .36, 1), opacity .3s ease, filter .3s ease; }
.ntt-leave-active { transition: transform .25s ease, opacity .25s ease; }
.ntt-enter { opacity: 0; transform: translateY(-12px) scale(.96); filter: blur(4px); }
.ntt-leave-to { opacity: 0; transform: translateX(24px) scale(.98); }
.ntt-move { transition: transform .3s cubic-bezier(.22, 1, .36, 1); }

@media (max-width: 760px) {
	.ntt { top: 68px; left: 10px; right: 10px; width: auto; max-width: none; }
	.ntt-card { border-radius: 18px; }
}
@media (prefers-reduced-motion: reduce) {
	.ntt-enter-active, .ntt-leave-active, .ntt-move { transition: opacity .2s; }
	.ntt-enter, .ntt-leave-to { transform: none; filter: none; }
}
@media print { .ntt { display: none; } }
</style>

<style lang="scss">
/* Con el panel de la campana abierto, los carteles no lo tapan */
html.nt-panel-abierto .ntt { opacity: 0; visibility: hidden; transition: opacity .2s, visibility 0s .2s; }
.ntt { transition: opacity .2s; }

/* Avisos de vue-toasted ($toasted.show, pantallas viejas): mismo diseño que los
   carteles de arriba, para que todos los avisos del sitio se vean iguales. */
.toasted-container .toasted {
	min-height: 0 !important; padding: 15px 18px 15px 46px !important;
	border-radius: 18px !important;
	background: rgba(255, 255, 255, .94) !important;
	-webkit-backdrop-filter: blur(18px) saturate(1.4); backdrop-filter: blur(18px) saturate(1.4);
	border: 1px solid rgba(29, 26, 23, .06) !important;
	box-shadow: 0 1px 2px rgba(29, 26, 23, .04), 0 14px 34px -16px rgba(122, 57, 0, .28), 0 32px 64px -36px rgba(29, 26, 23, .28) !important;
	color: #1d1a17 !important;
	font-family: 'DM Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif !important;
	font-size: 14px !important; font-weight: 600 !important; line-height: 1.45 !important;
	position: relative;
	&::before {
		content: ''; position: absolute; left: 18px; top: 50%; width: 14px; height: 14px; margin-top: -7px; border-radius: 50%;
		background: #ff7a00; box-shadow: 0 0 0 5px rgba(255, 122, 0, .14);
	}
	&.success::before { background: #1c7c45; box-shadow: 0 0 0 5px rgba(28, 124, 69, .14); }
	&.error::before { background: #c0392b; box-shadow: 0 0 0 5px rgba(192, 57, 43, .14); }
	&.info::before { background: #5d554e; box-shadow: 0 0 0 5px rgba(93, 85, 78, .12); }
	.action { color: #e8640a !important; font-weight: 700 !important; text-transform: none !important; border-radius: 999px !important; }
	.action:hover { background: #fff1e4 !important; text-decoration: none !important; }
}
.toasted-container.top-right { top: 88px !important; right: 20px !important; }
@media (max-width: 760px) {
	.toasted-container.top-right, .toasted-container.top-center { top: 68px !important; }
}
</style>
